import "./SystemAlertComponent.scss";
import template from "./SystemAlertComponent.html";
import {IComponentOptions} from "angular";
import {AlertLevel, SystemAlert} from "../model/SystemAlert";
import {Observable, ReplaySubject} from "rxjs";
import {LanguageService} from "../../../../translation/service/LanguageService";
import {TranslationsLanguage} from "../../../../translation/config/TranslationsLanguage";

export const SystemAlertComponent = "tfSystemAlert";

export function systemAlertComponent(): IComponentOptions {
    return {
        template,
        bindings: {
            alert: "<"
        },
        controllerAs: "ctrl",
        controller: SystemAlertComponentController
    };
}

export class SystemAlertComponentController {

    /** @input */
    public set alert(alert: SystemAlert | null) {
        this.alert$.next(alert);
    }

    private readonly alert$ = new ReplaySubject<SystemAlert | null>(1);

    public readonly isCriticalAlert: Observable<boolean>;
    public readonly isWarningAlert: Observable<boolean>;
    public readonly isInfoAlert: Observable<boolean>;
    public readonly alertClass: Observable<string | null>;
    public readonly message: Observable<string>;
    public readonly url: Observable<string | null>;

    /** @ngInject */
    public constructor(private readonly languageService: LanguageService) {
        this.isCriticalAlert = this.buildIsCriticalAlert();
        this.isWarningAlert = this.buildIsWarningAlert();
        this.isInfoAlert = this.buildIsInfoAlert();
        this.alertClass = this.buildAlertClass();
        this.message = this.buildMessage();
        this.url = this.buildUrl();
    }

    private buildIsCriticalAlert(): Observable<boolean> {
        return this.alert$
                   .map((alert) => alert?.level === AlertLevel.CRITICAL)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsWarningAlert(): Observable<boolean> {
        return this.alert$
                   .map((alert) => alert?.level === AlertLevel.WARNING)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsInfoAlert(): Observable<boolean> {
        return this.alert$
                   .map((alert) => alert?.level === AlertLevel.INFO)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildAlertClass(): Observable<string | null> {
        return this.alert$
                   .map((alert) => this.calculateAlertClass(alert))
                   .publishReplay(1)
                   .refCount();
    }

    private calculateAlertClass(alert: SystemAlert | null): string | null {
        if (alert == null) {
            return null;
        }
        switch (alert.level) {
            case AlertLevel.CRITICAL:
                return "system-critical-alert";
            case AlertLevel.WARNING:
                return "system-warning-alert";
            case AlertLevel.INFO:
                return "system-info-alert";
        }
    }

    private buildMessage(): Observable<string> {
        const source = Observable.combineLatest(this.alert$, this.languageService.current());

        return source.map(([alert, language]) => this.calculateMessage(alert, language))
                     .publishReplay(1)
                     .refCount();
    }

    private calculateMessage(alert: SystemAlert | null, language: TranslationsLanguage): string {
        if (alert == null) {
            return "";
        } else {
            return alert.messages[language];
        }
    }

    public buildUrl(): Observable<string | null> {
        return this.alert$
                   .map((a) => a?.url ?? null)
                   .publishReplay(1)
                   .refCount();
    }
}
